import React, { useRef, useMemo } from "react";
import { motion, useInView } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

const PRESETS = {
  fadeUp: {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0 },
  },
  fadeIn: {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  },
  fadeLeft: {
    hidden: { opacity: 0, x: -30 },
    visible: { opacity: 1, x: 0 },
  },
  fadeRight: {
    hidden: { opacity: 0, x: 30 },
    visible: { opacity: 1, x: 0 },
  },
  scaleIn: {
    hidden: { opacity: 0, scale: 0.92 },
    visible: { opacity: 1, scale: 1 },
  },
  lineGrow: {
    hidden: { scaleX: 0, originX: 0 },
    visible: { scaleX: 1 },
  },
};

export default function ScrollReveal({
  children,
  preset = "fadeUp",
  duration = 0.8,
  delay = 0,
  once = true,
  threshold = 0.15,
  className = "",
  style,
  as = "div",
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once, amount: threshold });
  const variants = PRESETS[preset] || PRESETS.fadeUp;
  const transition = useMemo(() => ({ duration, delay, ease: EASE }), [duration, delay]);
  const MotionTag = motion[as] || motion.div;

  return (
    <MotionTag
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={variants}
      transition={transition}
      className={className}
      style={style}
    >
      {children}
    </MotionTag>
  );
}

export function StaggerContainer({
  children,
  stagger = 0.12,
  once = true,
  threshold = 0.1,
  className = "",
  style,
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once, amount: threshold });
  const variants = useMemo(() => ({
    hidden: {},
    visible: { transition: { staggerChildren: stagger } },
  }), [stagger]);

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={variants}
      className={className}
      style={style}
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({
  children,
  preset = "fadeUp",
  duration = 0.7,
  className = "",
  style,
}) {
  const variants = PRESETS[preset] || PRESETS.fadeUp;
  const transition = useMemo(() => ({ duration, ease: EASE }), [duration]);

  return (
    <motion.div
      variants={variants}
      transition={transition}
      className={className}
      style={style}
    >
      {children}
    </motion.div>
  );
}
